import { TFunction } from 'i18next'
import { EventTypeLocationType, PublicBooking } from '../../../types'

export const browserTimezone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}

// 12h times render as cal.com does ("9:00am") instead of Intl's "9:00 AM"
export const formatTime = (iso: string, timezone: string, hour12: boolean, locale?: string) =>
  new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', hour12, timeZone: timezone }).format(new Date(iso))
    .replace(/\s?(AM|PM)$/i, m => m.trim().toLowerCase())

export const formatDateLong = (iso: string, timezone: string, locale?: string) =>
  new Intl.DateTimeFormat(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: timezone }).format(new Date(iso))

// YYYY-MM-DD for a calendar Date (react-day-picker gives local-midnight Dates).
// The slots response is already keyed by date in the requested timezone, so the
// calendar day is used as-is and no timezone conversion is needed here.
export const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export const fromDateKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export const locationLabel = (t: TFunction, location: EventTypeLocationType | string | null) => {
  switch (location) {
    case EventTypeLocationType.GOOGLE_MEET: return t('Scheduling::Google Meet')
    case EventTypeLocationType.MICROSOFT_TEAMS: return t('Scheduling::Microsoft Teams')
    case EventTypeLocationType.IN_PERSON_ORGANIZER: return t('Scheduling::In person (organizer address)')
    case EventTypeLocationType.IN_PERSON_ATTENDEE: return t('Scheduling::In person (attendee address)')
    case EventTypeLocationType.PHONE_ORGANIZER: return t('Scheduling::Phone call')
    case EventTypeLocationType.PHONE_ATTENDEE: return t('Scheduling::Attendee phone number')
    default: return null
  }
}

// Meeting link for Meet/Teams, otherwise the resolved address or phone number
export const bookingWhere = (booking: PublicBooking) => booking.meeting_link || booking.location_value || ''

export const mapsUrl = (address: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`

// RFC 5545 TEXT escaping; CR/LF stripped so values cannot inject extra properties.
const icsText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r\n|\r|\n/g, ' ')

const icsDate = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

export const googleCalendarUrl = (booking: PublicBooking) => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: booking.title,
    dates: `${icsDate(booking.start_time)}/${icsDate(booking.end_time)}`,
    details: booking.meeting_link || '',
    location: bookingWhere(booking),
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export const outlookCalendarUrl = (booking: PublicBooking, office365 = false) => {
  const base = office365 ? 'https://outlook.office.com' : 'https://outlook.live.com'
  const params = new URLSearchParams({
    path: '/calendar/action/compose', rru: 'addevent', subject: booking.title,
    startdt: booking.start_time, enddt: booking.end_time, body: booking.meeting_link || '', location: bookingWhere(booking),
  })
  return `${base}/calendar/0/deeplink/compose?${params.toString()}`
}

export const icsDataUrl = (booking: PublicBooking) => {
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Bizzey//Scheduling//EN', 'BEGIN:VEVENT',
    `UID:booking-${booking.uid}@bizzey`, `DTSTAMP:${icsDate(new Date().toISOString())}`,
    `DTSTART:${icsDate(booking.start_time)}`, `DTEND:${icsDate(booking.end_time)}`,
    `SUMMARY:${icsText(booking.title)}`, bookingWhere(booking) ? `LOCATION:${icsText(bookingWhere(booking))}` : '',
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean)
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`
}
